/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { css } from '@emotion/react';
import isPropValid from '@emotion/is-prop-valid';
import styled from '../../../styles/styled';
import { isNumber, isString } from '../../../util/type-check';
import { clamp } from '../../../util/helpers';
import { wrapStyles, normalizeToNumber, composeBreakpoints } from '../utils';
import { MAX_COL_WIDTH, MIN_COL_SPAN } from '../constants';
const baseStyles = css `
  box-sizing: border-box;
  float: left;
`;
const createGutterStyles = (theme, grid) => {
    const styles = `
      padding-left: calc(${grid.gutter} / 2);
      padding-right: calc(${grid.gutter} / 2);
    `;
    return wrapStyles(theme, grid.breakpoint, styles);
};
const gutterStyles = ({ theme }) => composeBreakpoints(createGutterStyles, theme, theme.grid);
const createSpanStyles = (theme, grid, span) => {
    const value = normalizeToNumber(span);
    const safeSpan = clamp(value, MIN_COL_SPAN, grid.cols);
    const styles = `
    width: ${(MAX_COL_WIDTH / grid.cols) * safeSpan}%;
  `;
    return wrapStyles(theme, grid.breakpoint, styles);
};
const spanStyles = ({ theme, span = 0 }) => isNumber(span) || isString(span)
    ? createSpanStyles(theme, theme.grid.default, span)
    : composeBreakpoints(createSpanStyles, theme, span);
const createSkipStyles = (theme, grid, skip) => {
    const value = normalizeToNumber(skip);
    const safeSkip = clamp(value, grid.cols * -1, grid.cols - 1);
    const styles = `
    left: ${(MAX_COL_WIDTH / grid.cols) * safeSkip}%;
    position: relative;
  `;
    return wrapStyles(theme, grid.breakpoint, styles);
};
const skipStyles = ({ theme, skip = 0 }) => isNumber(skip) || isString(skip)
    ? createSkipStyles(theme, theme.grid.default, skip)
    : composeBreakpoints(createSkipStyles, theme, skip);
/**
 * Content wrapping for the Grid component. Allows sizing based on provided
 * props.
 */
export const Col = styled('div', {
    shouldForwardProp: (prop) => isPropValid(prop) && prop !== 'span',
})(baseStyles, gutterStyles, spanStyles, skipStyles);
